import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Alert,
  Skeleton,
} from '@mui/material'
import {
  CheckCircle as CheckIcon,
} from '@mui/icons-material'
import { useRecentCheckins } from '../hooks/useRecentCheckins'
import { LeaderboardEntry } from '@api/types/leaderboard'

interface RecentCheckinsProps {
  adventureId: string
}

export function RecentCheckins({ adventureId }: RecentCheckinsProps) {
  const { data, isLoading, error } = useRecentCheckins(adventureId)

  const formatDate = (dateString: string): { date: string; time: string } => {
    const date = new Date(dateString)
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }
  }

  const renderHeader = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <CheckIcon color="success" />
      <Typography variant="subtitle1" fontWeight={600}>
        Recent Completions
      </Typography>
    </Box>
  )

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        {renderHeader()}
        <Skeleton variant="rectangular" height={200} />
      </Paper>
    )
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        {renderHeader()}
        <Alert severity="error">Failed to load recent completions</Alert>
      </Paper>
    )
  }

  if (!data) {
    return null
  }

  // Sort entries by completedAt descending (newest first)
  const sortedEntries = data.entries
    ? [...data.entries].sort((a, b) =>
        new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime()
      )
    : []

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {renderHeader()}

      {sortedEntries.length > 0 ? (
        <TableContainer>
          <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ px: 1 }}>Adventurer</TableCell>
                <TableCell align="right" sx={{ width: 100, px: 1 }}>Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEntries.map((entry: LeaderboardEntry) => {
                const displayName = entry.user.displayName || 'Unknown'
                const avatarUrl = entry.user.avatarUrl
                const { date, time } = formatDate(entry.completedAt)

                return (
                  <TableRow
                    key={entry.sessionId}
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <TableCell sx={{ px: 1, overflow: 'hidden' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <Avatar
                          sx={{ width: 24, height: 24, flexShrink: 0 }}
                          src={avatarUrl}
                        >
                          {displayName?.[0] || '?'}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {displayName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ px: 1 }}>
                      <Box>
                        <Typography variant="body2">
                          {date}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {time}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            No completions yet. Be the first!
          </Typography>
        </Box>
      )}

      {/* Footer: Total entries */}
      {data.totalEntries > 0 && (
        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {data.totalEntries} completion{data.totalEntries !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}
    </Paper>
  )
}
